import type {
  DesktopPraxaOrganizationPeopleBridge,
  PraxaDesktopInviteOrganizationPersonInput,
  PraxaDesktopOrganizationPeopleActionResult,
  PraxaDesktopOrganizationPeopleState,
  PraxaDesktopUpdateOrganizationMemberInput,
} from "@praxa/platform-contracts";
import { useCallback, useEffect, useRef, useState } from "react";

export const PRAXA_ORGANIZATION_PEOPLE_LOAD_ERROR =
  "Organization People couldn’t load. Local coding is still available.";

function getBridge(): DesktopPraxaOrganizationPeopleBridge | undefined {
  return typeof window === "undefined" ? undefined : window.desktopBridge?.praxaOrganizationPeople;
}

export function useDesktopPraxaOrganizationPeople(): {
  readonly action: PraxaDesktopOrganizationPeopleActionResult | null;
  readonly bridge: DesktopPraxaOrganizationPeopleBridge | undefined;
  readonly clearAction: () => void;
  readonly error: string | null;
  readonly invite: (
    input: PraxaDesktopInviteOrganizationPersonInput,
  ) => Promise<PraxaDesktopOrganizationPeopleActionResult | null>;
  readonly loading: boolean;
  readonly refresh: () => Promise<void>;
  readonly removeMember: (
    workspaceId: string,
    memberId: string,
  ) => Promise<PraxaDesktopOrganizationPeopleActionResult | null>;
  readonly revokeInvite: (
    workspaceId: string,
    inviteId: string,
  ) => Promise<PraxaDesktopOrganizationPeopleActionResult | null>;
  readonly state: PraxaDesktopOrganizationPeopleState | null;
  readonly updateMember: (
    input: PraxaDesktopUpdateOrganizationMemberInput,
  ) => Promise<PraxaDesktopOrganizationPeopleActionResult | null>;
  readonly working: boolean;
} {
  const bridge = getBridge();
  const [state, setState] = useState<PraxaDesktopOrganizationPeopleState | null>(null);
  const [action, setAction] = useState<PraxaDesktopOrganizationPeopleActionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const eventVersionRef = useRef(0);

  const applyState = useCallback((next: PraxaDesktopOrganizationPeopleState) => {
    setState(next);
    setError(null);
  }, []);

  const refresh = useCallback(async () => {
    if (!bridge) return;
    const eventVersion = eventVersionRef.current;
    setLoading(true);
    setError(null);
    try {
      const next = await bridge.refresh();
      if (eventVersionRef.current === eventVersion) applyState(next);
    } catch {
      if (eventVersionRef.current === eventVersion) {
        setError(PRAXA_ORGANIZATION_PEOPLE_LOAD_ERROR);
      }
    } finally {
      setLoading(false);
    }
  }, [applyState, bridge]);

  useEffect(() => {
    if (!bridge) {
      setState(null);
      setAction(null);
      setError(null);
      setLoading(false);
      return;
    }
    let active = true;
    let unsubscribe = () => {};
    try {
      unsubscribe = bridge.onStateChange((next) => {
        eventVersionRef.current += 1;
        if (active) applyState(next);
      });
    } catch {
      setError(PRAXA_ORGANIZATION_PEOPLE_LOAD_ERROR);
    }
    const eventVersion = eventVersionRef.current;
    setLoading(true);
    void bridge
      .getState()
      .then((next) => {
        if (active && eventVersionRef.current === eventVersion) applyState(next);
      })
      .catch(() => {
        if (active && eventVersionRef.current === eventVersion) {
          setError(PRAXA_ORGANIZATION_PEOPLE_LOAD_ERROR);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [applyState, bridge]);

  const invoke = useCallback(
    async (
      operation: (
        activeBridge: DesktopPraxaOrganizationPeopleBridge,
      ) => Promise<PraxaDesktopOrganizationPeopleActionResult>,
    ): Promise<PraxaDesktopOrganizationPeopleActionResult | null> => {
      if (!bridge) return null;
      setWorking(true);
      setAction(null);
      try {
        const result = await operation(bridge);
        setState(result.state);
        setAction(result);
        setError(null);
        return result;
      } catch {
        setError("Praxa couldn’t complete this organization change.");
        return null;
      } finally {
        setWorking(false);
      }
    },
    [bridge],
  );

  return {
    action,
    bridge,
    clearAction: () => setAction(null),
    error,
    invite: (input) => invoke((activeBridge) => activeBridge.invite(input)),
    loading,
    refresh,
    removeMember: (workspaceId, memberId) =>
      invoke((activeBridge) => activeBridge.removeMember(workspaceId, memberId)),
    revokeInvite: (workspaceId, inviteId) =>
      invoke((activeBridge) => activeBridge.revokeInvite(workspaceId, inviteId)),
    state,
    updateMember: (input) => invoke((activeBridge) => activeBridge.updateMember(input)),
    working,
  };
}
